import React from "react";
import { NavigationServer } from "./NavigationServer";
import { siteConfig } from "../utils/config";
import {
  formatPhoneNumber,
  formatPhoneNumberForTel,
} from "../utils/formatters";

// This is a shell that renders the server-side navigation during SSR
// and will be hydrated with the client-side version in the browser
export function Navigation() {
  if (typeof window === "undefined") {
    // We're on the server, render the server-side version
    return <NavigationServer />;
  }

  // We'll render a client-only version below that gets hydrated on the client
  return <NavigationClient />;
}

// Client-side only implementation with full interactivity
// This code will never run during SSR
function NavigationClient() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isScrolled, setIsScrolled] = React.useState(false);

  // Get the current path
  let currentPath = window.location.pathname;

  // Effect to handle scroll events
  React.useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener("scroll", handleScroll);

    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Navigation links - removed Home
  const links = [
    { path: "/how-it-works", label: "How It Works" },
    { path: "/why-us", label: "Why Us" },
    { path: "/about", label: "About" },
    { path: "/faq", label: "FAQ" },
    { path: "/contact", label: "Contact" },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      {/* Top Contact Bar */}
      <div
        className={`bg-accent text-dark-green-800 transition-all duration-300 ${
          isScrolled ? "py-0.5" : "py-1"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-end items-center">
            <div className="flex items-center text-xs">
              <svg
                className="h-3 w-3 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              <span className="hidden sm:inline mr-1">Call for a quote:</span>
              <a
                href={`tel:${formatPhoneNumberForTel(siteConfig.contactPhone)}`}
                className="font-medium hover:underline"
              >
                {formatPhoneNumber(siteConfig.contactPhone)}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav
        className={`transition-all duration-300 ${
          isScrolled ? "bg-dark-green-800 shadow-md" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`flex justify-between ${isScrolled ? "h-16" : "h-20"} transition-all duration-300`}
          >
            <div className="flex items-center">
              {/* Website Logo */}
              <div className="flex-shrink-0 flex items-center">
                <a href="/" className="flex items-center">
                  <img
                    src="/logo.png"
                    alt="Shingle Guard Logo"
                    className={`${isScrolled ? "h-8 w-8" : "h-10 w-10"} transition-all duration-300`}
                  />
                  <span
                    className={`ml-2 ${isScrolled ? "text-xl" : "text-2xl"} font-medium text-white transition-all duration-300 font-heading`}
                  >
                    Shingle
                    <span className="text-accent font-heading font-medium">
                      Guard
                    </span>
                  </span>
                </a>
              </div>

              {/* Primary Nav */}
              <div className="hidden lg:ml-10 lg:flex lg:space-x-6">
                {links.map((link) => (
                  <a
                    key={link.path}
                    href={link.path}
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-md font-light ${isScrolled ? "h-16" : "h-20"} transition-all duration-300 ${
                      currentPath === link.path
                        ? "border-accent text-accent"
                        : "border-transparent text-gray-100 hover:text-white hover:border-gray-400"
                    }`}
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>

            {/* Secondary Nav - Contact Button */}
            <div className="hidden lg:flex items-center space-x-1">
              <a
                href="/contact"
                className={`inline-flex items-center justify-center px-6 py-2.5 border border-transparent text-sm font-light rounded-full shadow-md text-dark-green-800 bg-accent hover:bg-yellow-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-all duration-300 ${
                  isScrolled ? "scale-95" : ""
                } hover:scale-105 hover:-translate-y-1`}
              >
                Get Free Estimate
              </a>
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden flex items-center">
              <button
                className="mobile-menu-button p-2 rounded-md text-gray-100 hover:text-accent focus:outline-none"
                onClick={toggleMobileMenu}
                aria-label="Toggle menu"
              >
                <svg
                  className="h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden ${isScrolled ? "bg-dark-green-800" : "bg-dark-green-700"} shadow-md border-t border-dark-green-600 overflow-hidden transition-all duration-300 ease-in-out ${
          isMobileMenuOpen ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="py-2">
          {links.map((link) => (
            <a
              key={link.path}
              href={link.path}
              className={`block py-3 px-4 text-base font-medium ${
                currentPath === link.path
                  ? "text-accent bg-dark-green-800"
                  : "text-gray-100 hover:bg-dark-green-800 hover:text-white"
              }`}
            >
              {link.label}
            </a>
          ))}
          <div className="pt-4 pb-3 border-t border-dark-green-600 px-4">
            <a
              href="/contact"
              className="block w-full text-center px-6 py-3.5 text-base body-text text-dark-green-800 bg-accent hover:bg-yellow-500 rounded-full shadow-md transition-all duration-300 hover:scale-105 hover:-translate-y-1"
            >
              Get Free Estimate
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
